import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  faCogs,
  faExchangeAlt,
  faHeadset,
  faSignOutAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Avatar,
  Button,
  Divider,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { Brightness2Outlined, WbSunnyOutlined } from '@material-ui/icons'
import { LanguageButton } from 'components/Localization/LanguageButton'
import { useDialogManager } from 'features/DialogManager/DialogManager'
import { useAppRunningContext } from 'features/EmbeddedRh24/useAppRunningContext'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { SetDefaultTheme } from 'store/Application/appActions'
import { RootAppState } from 'store/configureStore'
import { BuiltInThemes } from 'utils/appTheme'
import { useOrganizationContext } from 'utils/useOrganizationContext'
import { Profile } from 'model/User'
import { PartySummaryDto } from 'services/APIs/InternalAPI/internal-api.contracts'

import './UserButton.sass'
import { useController } from 'customHooks/useController'
import { UserController } from 'features/CustomerPortal/ConnectedParties/controllers/UserController'
import { newUserActions } from 'store/User/newUserReducer'

const mapStateToProps = (state: RootAppState) => ({
  userProfile: state.user.profile,
  organizationContext: state.user.organizationContext,
  themeName: state.app.builtInThemeName,
})

const mapDispatchToProps = (dispatch) => ({
  clearOrganizationContext: () =>
    dispatch(newUserActions.clearOrganizationContext()),
  changeBuiltInTheme: (themeName: BuiltInThemes) =>
    dispatch(SetDefaultTheme(themeName)),
})

type userButtonProps = {
  userProfile: Profile
  organizationContext: PartySummaryDto
  clearOrganizationContext: () => void
  themeName: string
  changeBuiltInTheme: (themeName: BuiltInThemes) => void
}

const UserButton = (props: userButtonProps) => {
  const navigate = useNavigate()
  const [isExpanded, setExpanded] = useState(false)
  const anchorEl = useRef(undefined)
  const { withinIframe } = useAppRunningContext()
  const { openDialog } = useDialogManager()

  const { controller } = useController(() => new UserController())

  const { t } = useTranslation()

  const handleContextMenu = () => {
    setExpanded(!isExpanded)
  }

  const handleLogOut = async () => {
    navigate('/logout')
  }

  const menuAction = (action) => {
    action()
    setExpanded(false)
  }

  const { organizationId } = useOrganizationContext()

  const profileOrSettingsUrl = useMemo(
    () => (organizationId ? '/app/settings' : '/app/user'),
    [organizationId]
  )

  return (
    <div ref={anchorEl} style={{ display: 'flex', alignItems: 'center' }}>
      <Button
        className="user-button--menu-item is-fullheight"
        name="rh24-menu-button"
        onClick={handleContextMenu}
        color="inherit"
      >
        <div className="user-button">
          <ListItemText
            primary={
              <Typography variant="body1" noWrap>
                {`${props.userProfile?.firstName || ''} ${
                  props.userProfile?.lastName
                }`.trim()}
              </Typography>
            }
            secondary={`${
              props.organizationContext?.name ??
              t('common:select-organization', 'select an organization')
            }`}
            secondaryTypographyProps={{ color: 'inherit' }}
            className="user-text"
          />
          <ListItemAvatar>
            <Avatar
              src={props.userProfile?.picture}
              variant="circular"
              className="avatar"
            ></Avatar>
          </ListItemAvatar>
        </div>
      </Button>
      <Menu
        id="user_menu"
        anchorEl={anchorEl.current}
        open={isExpanded}
        getContentAnchorEl={null}
        onClose={() => setExpanded(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.preventDefault()
            menuAction(() => navigate(profileOrSettingsUrl))
          }}
          onAuxClick={(e) => {
            e.preventDefault()
            window.open(profileOrSettingsUrl, '_blank')
          }}
        >
          <ListItemIcon>
            <FontAwesomeIcon icon={faCogs as IconProp} />
          </ListItemIcon>
          <ListItemText primary={t('common:settings', 'settings')} />
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            e.preventDefault()
            menuAction(() => {
              controller.ClearUserOrganizationContext()
              navigate('/app/workspaces')
            })
          }}
          onAuxClick={(e) => {
            e.preventDefault()
            controller.ClearLastVisitedWorkspace()
            window.open('/app/workspaces', '_blank')
          }}
        >
          <ListItemIcon>
            <FontAwesomeIcon icon={faExchangeAlt as IconProp} />
          </ListItemIcon>
          <ListItemText
            primary={t('common:switch-workspace', 'switch workspace')}
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => menuAction(() => handleLogOut())}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faSignOutAlt as IconProp} />
          </ListItemIcon>
          <ListItemText primary={t('common:logout', 'logout')} />
        </MenuItem>
        {!withinIframe ? (
          <span>
            <Divider />
            <MenuItem
              onClick={() => menuAction(() => openDialog('SupportContact'))}
            >
              <ListItemIcon>
                {/* <ContactSupportOutlined /> */}
                <FontAwesomeIcon icon={faHeadset as IconProp} />
              </ListItemIcon>
              <ListItemText primary={t('common:get-support', 'get support')} />
            </MenuItem>
          </span>
        ) : null}
        {!withinIframe && (
          <span>
            <Divider />
            <MenuItem
              onClick={() =>
                props.themeName === 'light'
                  ? props.changeBuiltInTheme('darkBlue')
                  : props.changeBuiltInTheme('light')
              }
            >
              <ListItemIcon>
                {props.themeName === 'light' ? (
                  <WbSunnyOutlined />
                ) : (
                  <Brightness2Outlined />
                )}
              </ListItemIcon>
              <ListItemText
                primary={t('common:change-app-theme', 'change app theme')}
              />
            </MenuItem>
          </span>
        )}
        <LanguageButton variant="menu-item" />
      </Menu>
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(UserButton)
